import { Link, useRouterState } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { useUiStore } from "../stores/ui";
import { useQuery } from "@tanstack/react-query";
import { companiesApi } from "../api/queries/companies";
import { authApi } from "../api/queries/auth";

const PAGES: Array<{ to: string; label: string }> = [
  { to: "/", label: "Dashboard" },
  { to: "/activity", label: "Activity" },
  { to: "/work-orders", label: "Work orders" },
  { to: "/settings", label: "Settings" },
];

export function TopNav() {
  const setActiveCompanyId = useUiStore((s) => s.setActiveCompanyId);
  const activeCompanyId = useUiStore((s) => s.activeCompanyId);
  const routerState = useRouterState();
  const path = routerState.location.pathname;

  const me = useQuery({ queryKey: ["me"], queryFn: () => authApi.me() });
  const companies = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });

  const active = companies.data?.companies.find((c) => c.id === activeCompanyId)
    ?? companies.data?.companies[0];

  if (active && active.id !== activeCompanyId) {
    queueMicrotask(() => setActiveCompanyId(active.id));
  }

  const initials = (s: string) =>
    s
      .split(/[\s-]+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <header className="topnav">
      <div className="topnav__left">
        <Link to="/" className="brand" aria-label="Paperclip Cloud home">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M16.5 6.5v9a4.5 4.5 0 0 1-9 0v-9a3 3 0 0 1 6 0v8a1.5 1.5 0 0 1-3 0v-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="brand__name">Paperclip</span>
        </Link>

        <span className="divider-v" aria-hidden="true" />

        <button className="ws-switcher" aria-haspopup="menu" type="button">
          <span className="ws-switcher__avatar" aria-hidden="true">
            {active ? initials(active.name) : "··"}
          </span>
          <span className="ws-switcher__labels">
            <span className="ws-switcher__name">{active?.name ?? "No company"}</span>
            <span className="ws-switcher__role">
              {active
                ? `${active.role} · ${active.device_count} device${active.device_count === 1 ? "" : "s"}`
                : "Create your first company"}
            </span>
          </span>
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className="ws-switcher__chev">
            <path
              d="M4 6l4 4 4-4M4 10l4-4 4 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <nav className="primary-nav" aria-label="Primary">
          {PAGES.map((p) => {
            const isActive =
              p.to === "/"
                ? path === "/" || path.startsWith("/devices")
                : path.startsWith(p.to);
            return (
              <Link
                key={p.to}
                to={p.to}
                className={`navlink${isActive ? " navlink--active" : ""}`}
              >
                {p.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="topnav__right">
        <div className="search">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M10.5 10.5L13 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            id="global-search-input"
            type="text"
            placeholder="Search devices, orders…"
            aria-label="Search"
          />
          <kbd>/</kbd>
        </div>

        <button className="iconbtn" aria-label="Notifications" title="Notifications">
          <Bell size={16} />
        </button>

        <ThemeToggle />

        <div
          className="me"
          aria-label={`Signed in as ${me.data?.user?.display_name ?? me.data?.user?.email ?? "you"}`}
          title={me.data?.user?.email ?? ""}
        >
          {me.data?.user ? initials(me.data.user.display_name || me.data.user.email) : "·"}
        </div>
      </div>
    </header>
  );
}
